/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA;
 */
import { tabKeys, ltiClaims } from '@/core/utils/consts';
import { get } from 'svelte/store';
import { decodedAccessToken } from '@/store/accessToken';
import * as Types from '@/core/model/types.js'; // eslint-disable-line no-unused-vars

/**
 * Gets array of available ACS for received delivery execution status. If ACS are disabled
 * by feature flag returns empty array
 * @param {string} tabKey - the current tab (equates to the status of delivery execution(s))
 * @param {Types.Interaction[]?} interactions - some actions are available only for certain deliveries;
 *    if null/undefined, then all delivery-dependent actions are available
 * @returns {Array} - Array of available ACS
 */
export default function (tabKey, interactions = null) {
    const accessToken = get(decodedAccessToken);
    let availableActions = accessToken[ltiClaims.custom]?.availableActions || [];
    // some platform sends availableActions as string
    if (typeof availableActions === 'string') {
        try {
            availableActions = JSON.parse(availableActions);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error parsing availableActions', e);
        }
    }
    if (availableActions.includes('extend_time')) {
        availableActions.push('reset_extra_time');
    }
    let enabledActions = [];
    if (tabKey === tabKeys.awaitingAuthorization) {
        return ['authorize'];
    } else if (tabKey === tabKeys.inProgress) {
        // these list orders are reflected in the UI
        enabledActions = ['terminate', 'extend_time', 'reset_extra_time', 'pause', 'reset', 'report'];
    } else if (tabKey === tabKeys.paused) {
        enabledActions = ['terminate', 'extend_time', 'reset_extra_time', 'resume', 'reset', 'report'];
    } else if (tabKey === tabKeys.finished) {
        enabledActions = ['reopen', 'report'];
    }

    if (interactions && !interactions.some(i => i?.isTimerExists)) {
        enabledActions = enabledActions.filter(
            enabledAction => enabledAction !== 'extend_time' && enabledAction !== 'reset_extra_time'
        );
    } else if (interactions && !interactions.some(isResetExtraTimeAvailable)) {
        enabledActions = enabledActions.filter(enabledAction => enabledAction !== 'reset_extra_time');
    }

    return enabledActions.filter(enabledAction => availableActions.includes(enabledAction));
}

/**
 * Tells if 'resume' ACS is enabled in the current session
 * @returns {Boolean}
 */
export function getIsResumeEnabled() {
    const accessToken = get(decodedAccessToken);
    const availableActions = accessToken[ltiClaims.custom]?.availableActions || [];
    return availableActions.includes('resume');
}

/**
 * Checks if reset extra time is available for the current interaction
 * @param {object} interaction
 * @returns {boolean}
 */
export function isResetExtraTimeAvailable(interaction) {
    const extraTimer = interaction?.timers?.extra;
    if (!extraTimer) {
        return false;
    }

    const { maxTime, initialValue = 0, maxTimeRemaining } = extraTimer;
    return !!(
        (
            maxTime &&
            initialValue !== maxTime && // proctor gave extra time
            maxTimeRemaining > initialValue
        ) // proctor extra time is not fully consumed
    );
}
